/* eslint-disable @typescript-eslint/no-explicit-any */
import { WorldMapMarkerStory_worldMapView_markers } from 'graphql/query/__generated__/WorldMapMarkerStory';
import { useMarkerVisibilityView } from 'hooks/map/useMarkerVisibilityView';
import { mapGraphQLMarkers } from 'libs/utils/mapping/worldGrahQLMaps';
import { useAnalytics } from 'libs/utils/site/analytics';
import _ from 'lodash';
import { SeafoodButtons } from 'pages/custom/Seafood/SeafoodButtons';
import { CustomMapPage } from 'pages/custom/shared/CustomMapPage';
import { OverflowFilters } from 'pages/custom/shared/overflow/OverflowFilters';
import { S_PageStyle } from 'pages/page/PageStyle';
import { useContext } from 'react';
import { IWorldMarker } from 'react-give-me-map';
import { CtxModal } from 'session/ctxModal';
import { CtxView } from 'session/ctxView';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
}

const EAT_SET = 'd3285c4e-d219-49e7-83bb-e153b684bd83';
const EXPLORE_SET = 'b470fd3c-459e-48fe-9d14-b58f0632b34c';
const EXPERIENCE_SET = '687dd952-fc0d-4bf1-9a68-722bf702bc5f';
const LEARN_SET = 'd17c2db5-9d14-4427-b844-32b09beda821';

const GROUP_MARKER_1 = '0.64132882174266';
const GROUP_MARKER_2 = '0.6771997378364232';
const GROUP_MARKERS = [GROUP_MARKER_1, GROUP_MARKER_2];
const GROUP_TO_STORIES: Record<string, string[]> = {
    [GROUP_MARKER_1]: [
        '45e15562-c309-4ff5-9947-695661d86362',
        'f39ca99d-5099-49d7-a542-54ca9bfcd075',
        '52b3b1e2-2c6b-4824-87d9-8b9c42ce8469',
        '204bc6ff-2e7d-4e59-85d6-aa95b6bd9172',
        'b1b0b787-6a59-4ec8-92b1-ac6857b47f6d',
        'd3291f44-5149-4981-9f02-1cf0e8eb79c6',
        '79cd1371-aff2-4952-9953-3a6a1d301fac',
        'e3c95374-8fed-43fd-a36a-47897e5f4821',
        'ae35f5b1-f5eb-4623-8ec9-03c46f2ed0b0',
        '73600f8d-a99d-41d0-87e5-99dc9695ff8b',
        'facec7e1-ba80-4203-9a39-ce9b0870fd7b',
        '37161f40-2938-464d-8fd4-0166509733b2',
        '18232e57-fb3a-4503-bb13-1825a27ee28f',
        'ed461f8e-8824-47c0-bbbe-e7c72543091e',
        'a4fd607c-fcf1-4331-836c-24496962e073',
        '6c7e409d-e556-469a-b55d-d377bcb72b82',
        'aa056a7d-3503-4a68-954a-04170fcd3433'
    ],
    [GROUP_MARKER_2]: [
        '3c4c301f-597f-4c96-ab4d-3ebc037ef0a7',
        '35fa40f4-abbd-414b-9de3-d83cb00228d0',
        '66b67906-f647-424f-836f-b5bc002a923e',
        '14caafff-b0d3-40ee-b32d-122891ed630a',
        '37161f40-2938-464d-8fd4-0166509733b2',
        '18232e57-fb3a-4503-bb13-1825a27ee28f',
        'b1350b2c-377f-4cdf-8cf3-9c263f28db08',
        '8b69b7e2-8786-4aab-ae10-02b733b88492',
        '52b3b1e2-2c6b-4824-87d9-8b9c42ce8469',
        'd37af972-1761-43c4-ad10-ab0bfed5a371',
        '77121dd0-962c-417f-8159-dc21a2251a4d',
        '73600f8d-a99d-41d0-87e5-99dc9695ff8b',
        'e3c95374-8fed-43fd-a36a-47897e5f4821',
        '6c76f681-2944-48fa-9418-0683756e3737',
        'c7aa8fca-b737-4d1c-bda4-adc0fcc95c43',
        '43c11939-6a2e-4fa6-bf31-696bb98a566d'
    ]
};

export const SeefoodPage = (props: Props): JSX.Element => {
    const ctxView = useContext(CtxView);
    const ctxModal = useContext(CtxModal);
    const markerVisibility = useMarkerVisibilityView();
    const analytics = useAnalytics();

    function setAllVisible() {
        ctxModal.setShowListOfStories(false);
        ctxView.setVisibleMapSetIds([EAT_SET, EXPLORE_SET, EXPERIENCE_SET, LEARN_SET]);
        analytics.track('custom_button.clicked', {
            action: 'filter.reset'
        });
    }

    function setVisibleMapSet(mapSetId: string) {
        ctxModal.setShowListOfStories(false);
        ctxView.setVisibleMapSetIds([mapSetId]);
        analytics.track('custom_button.clicked', {
            action: 'filter.select',
            categoryId: mapSetId
        });
    }

    function isSetHighlighted(mapSetId: string) {
        return ctxView.visibleMapSetIds.length === 1 && ctxView.visibleMapSetIds[0] === mapSetId;
    }

    function modifyMarkers(markers: WorldMapMarkerStory_worldMapView_markers[]) {
        const updatedMarkers = _.clone(markers);
        markers.forEach((marker, index) => {
            if (marker.worldMapSets.length >= 2) {
                if (ctxView.visibleMapSetIds?.length === 1 && marker.type === 'PIN') {
                    if (checkIfProperSet(marker, EAT_SET)) {
                        updatedMarkers[index] = {
                            ...marker,
                            data: {
                                ...marker.data,
                                icon: {
                                    backgroundColor: '569AB5',
                                    iconColor: 'ffffff',
                                    iconText: 'utensils'
                                }
                            } as any
                        };
                    } else if (checkIfProperSet(marker, EXPLORE_SET)) {
                        updatedMarkers[index] = {
                            ...marker,
                            data: {
                                ...marker.data,
                                icon: {
                                    backgroundColor: '254765',
                                    iconColor: 'ffffff',
                                    iconText: 'binoculars'
                                }
                            } as any
                        };
                    } else if (checkIfProperSet(marker, EXPERIENCE_SET)) {
                        updatedMarkers[index] = {
                            ...marker,
                            data: {
                                ...marker.data,
                                icon: {
                                    backgroundColor: 'A2C034',
                                    iconColor: 'ffffff',
                                    iconText: 'sparkles'
                                }
                            } as any
                        };
                    } else if (checkIfProperSet(marker, LEARN_SET)) {
                        updatedMarkers[index] = {
                            ...marker,
                            data: {
                                ...marker.data,
                                icon: {
                                    ...(marker.data['icon'] as any),
                                    backgroundColor: '5FAA9B',
                                    iconColor: 'ffffff',
                                    iconText: 'graduation-cap'
                                } as any
                            } as any
                        };
                    }
                }
            }
        });

        return updatedMarkers ? mapGraphQLMarkers(updatedMarkers) : [];

        function checkIfProperSet(marker: WorldMapMarkerStory_worldMapView_markers, setId: string) {
            return (
                ctxView.visibleMapSetIds?.includes(setId) &&
                marker.worldMapSets?.find((set) => set.worldMapSetId === setId)
            );
        }
    }

    const preliminaryMarkers = (
        ctxView.allMarkers ?? []
    ).map<WorldMapMarkerStory_worldMapView_markers>((marker) => ({
        ...marker,
        selectable:
            markerVisibility.isMarkerSelectableInView(marker) ||
            GROUP_MARKERS.includes(marker.worldMapMarkerId),
        visible:
            markerVisibility.isMarkerVisibleInViewMap(marker) ||
            GROUP_MARKERS.includes(marker.worldMapMarkerId)
    }));

    // Go through the preliminary ones and hide Group markers that don't have any children visible
    const markers = preliminaryMarkers.map((marker) => {
        if (GROUP_MARKERS.includes(marker.worldMapMarkerId)) {
            const childMarkers = preliminaryMarkers.filter((e) =>
                GROUP_TO_STORIES[marker.worldMapMarkerId].includes(e.story?.storyId)
            );
            return {
                ...marker,
                visible: childMarkers.some((e) =>
                    markerVisibility.isMarkerStoryVisibleInViewList(e)
                )
            };
        }
        return marker;
    });

    const modifiedMarkers: IWorldMarker[] = modifyMarkers(markers);

    function openContent(ids: string[]) {
        const storyIdsToShow = markers
            .filter(
                (marker) =>
                    markerVisibility.isMarkerStoryVisibleInViewList(marker) &&
                    ids.includes(marker.story?.storyId)
            )
            .map((e) => e.story?.storyId);
        ctxModal.setVisibleStoryIds(storyIdsToShow);
        ctxModal.setShowListOfStories(true);
    }

    return (
        <S_SeefoodPage className={props.className}>
            <CustomMapPage
                mapId={'19b92a28-a263-4dbb-8c4c-d0a2700b5081'}
                header={{
                    logo: 'https://norwayseafoodfestival.no/wp-content/themes/norseafoodfest/images/logo-landscape-red.svg',
                    link: 'https://norwayseafoodfestival.no/en/frontpage/',
                    linkText: Localization.WEBSITE,
                    logoMarginTop: '-11px'
                }}
                controls={<SeafoodButtons />}
                overrideMapMarkers={modifiedMarkers ?? []}
                onMarkerSelected={(markerId: string) => {
                    if (markerId === GROUP_MARKER_1 || markerId === GROUP_MARKER_2) {
                        openContent(GROUP_TO_STORIES[markerId]);
                        return true;
                    }
                }}
                topOverflow={
                    <OverflowFilters
                        highlightedBackground="linear-gradient(270deg, rgb(173 64 23) 0%, rgb(200 102 65) 100%)"
                        items={[
                            {
                                iconName: 'location-dot',
                                text: Localization.ALL,
                                onClick: setAllVisible,
                                highlighted: ctxView.visibleMapSetIds.length === 4
                            },
                            {
                                iconName: 'utensils',
                                text: Localization.EAT,
                                onClick: () => {
                                    setVisibleMapSet(EAT_SET);
                                },
                                highlighted: isSetHighlighted(EAT_SET)
                            },
                            {
                                iconName: 'binoculars',
                                text: Localization.EXPLORE,
                                onClick: () => {
                                    setVisibleMapSet(EXPLORE_SET);
                                },
                                highlighted: isSetHighlighted(EXPLORE_SET)
                            },
                            {
                                iconName: 'sparkles',
                                text: Localization.EXPERIENCE,
                                onClick: () => {
                                    setVisibleMapSet(EXPERIENCE_SET);
                                },
                                highlighted: isSetHighlighted(EXPERIENCE_SET)
                            },
                            {
                                iconName: 'graduation-cap',
                                text: Localization.LEARN,
                                onClick: () => {
                                    setVisibleMapSet(LEARN_SET);
                                },
                                highlighted: isSetHighlighted(LEARN_SET)
                            }
                        ]}
                    />
                }
            />
        </S_SeefoodPage>
    );
};

const S_SeefoodPage = styled(S_PageStyle)`
    display: flex;
    flex-direction: column;
`;
